/* ============================================================
   DUAL PLAYER — a reactive MECHANISM over TWO YouTube frames.
   ------------------------------------------------------------
   WHY: the app exists because YouTube playlists break autoplay
   past 200 videos. Leaning on YT's playlist engine (loadPlaylist)
   quietly reintroduced that cap. So we drive the queue OURSELVES:
   one explicit video per frame. No playlist, no 200 wall.

   WHY TWO FRAMES: one frame plays video[i] while the other silently
   buffers the next, so a swap is a near-instant cut with no spin-up.

   This module makes NO playback decisions — the reconciler
   (src/tv/reconciler.ts) does. The DualPlayer just exposes the two
   frames' state (snapshot) + one onChange tap, and the idempotent
   primitives the reconciler issues: ensureBackground / swapToBackground
   (seek to a known offset + play) / seekForeground / playForeground /
   silenceForeground / blankFrames / adoptQueue. It owns the Tubes, the
   queue, the index, and which frame is visible (opacity on the hosts).

   AUDIO CAVEAT: unmuted audio needs user-activation. The "whack"
   grants the page sticky activation; the active frame unmutes on
   swap via delegated autoplay. Solid in Chrome; Safari/Firefox are
   the ones to test — a swapped-in frame may come up muted there.
   ============================================================ */
import * as Sentry from '@sentry/react'
import { Tube } from './youtube'
import type { StagePresenters } from './presenters'

export interface DualProgram {
  index: number
  total: number
  videoId: string
  title: string
  /** True when the current queue entry is an AD slot (no video loaded). */
  isAd: boolean
}

/**
 * A queue entry as the player sees it: a video id, or `null` for an AD slot.
 * The player only needs to know "is there a video to load here?"; the ad's
 * scene/params/duration live in the store and are driven by the reconciler.
 */
export type QueueEntry = string | null

export interface DualPlayerCallbacks {
  /** Both frames are constructed and ready to accept videos. */
  onReady?: () => void
  /** The current program changed (index/title) — push to the store. */
  onProgram?: (p: DualProgram) => void
  /** A frame errored on its video (after retries) — the reconciler routes around it. */
  onError?: (videoId: string) => void
  /**
   * EITHER frame's YT state changed (active or standby). The reactive reconciler
   * subscribes here to re-evaluate desired-vs-actual on every observable change —
   * a pure "something moved, re-check" tap.
   */
  onChange?: () => void
}

/** A reactive snapshot of both frames, for the reconciler's "actual" input. */
export interface DualSnapshot {
  fgId: string | null // foreground (active) video id
  fgState: number // foreground YT state
  fgTime: number // foreground currentTime (s)
  bgId: string | null // background (standby) video id
  bgReady: boolean // background cued/buffered → ready to swap in
}

type Slot = 'A' | 'B'

const YT_PLAYING = 1
const YT_BUFFERING = 3
// A drift seek onto a genuinely-playing frame already this close to the target is a
// no-op — re-seeking inside the slop just thrashes the player (the live stutter loop).
const SEEK_DEDUPE_S = 0.5
// Clamp a live-restore seek this many seconds short of the video's (known,
// Reload a failing active video this many times before skipping it.
export const MAX_LOAD_RETRIES = 4
// Backoff between reload attempts (× attempt #); 4 retries ≈ <1.5s, comfortably
// under kickPlay's ~12s before we mark the video dead.
export const LOAD_RETRY_BASE_MS = 300

export class DualPlayer {
  private a: Tube
  private b: Tube
  private hostA: HTMLElement
  private hostB: HTMLElement
  private cb: DualPlayerCallbacks
  private readyCount = 0

  private queue: QueueEntry[] = []
  private index = 0
  private active: Slot = 'A'
  private wantPlaying = false // whether we should be playing (vs cued/paused)
  private volume = 70
  private muted = false
  private subtitlesEnabled = false
  private audioUnlocked = false
  // What each frame currently holds, and whether that's a FRESH cue sitting at
  // t=0 (vs. a frame that has since been the active/playing one). This lets an
  // auto-advance ADOPT the already-pre-cued standby atomically instead of
  // re-cueing it — re-cueing a warm standby resets its buffer at the exact
  // moment we need it instant.
  private slotVideo: Record<Slot, string | null> = { A: null, B: null }
  private slotFresh: Record<Slot, boolean> = { A: false, B: false }
  // True while a cover (channel bumper / program ident) is shown over the foreground.
  // The executor sets it; the auto-unmute paths (swap / kickPlay / onState PLAYING) must
  // NOT lift the mute while covered, so audio can't leak under the bumper. Reveal clears it.
  private covered = false
  private subtitlesRebuildToken = 0
  private playKickTimer = 0
  private playKickToken = 0
  // Active-frame load-error retries: reload the SAME video a few times (many
  // YouTube code-2s are transient embed hiccups) before giving up and skipping.
  // Tracked per video id; reset when the active video changes or genuinely plays.
  private activeRetries = 0
  private activeRetryId: string | null = null
  private activeRetryTimer = 0
  // The Stage: the DualPlayer also owns the two OTHER full-frame presentables — a bump SCENE (ad /
  // ad-channel / tune cover) and a React COMPONENT (HIDEO) — injected so this layer doesn't import
  // Three.js / React. Exactly one presentable is on screen; switching to another TEARS DOWN the rest, so
  // a stale layer is impossible by construction (the smell that motivated this).
  private presenters?: StagePresenters
  private presentable: 'video' | 'scene' | 'component' = 'video'

  constructor(
    hostA: HTMLElement,
    hostB: HTMLElement,
    cb: DualPlayerCallbacks = {},
    presenters?: StagePresenters,
  ) {
    this.hostA = hostA
    this.hostB = hostB
    this.cb = cb
    this.presenters = presenters
    // The YT IFrame API REPLACES the element it's given with the <iframe>. So we
    // give it a throwaway inner mount and keep the host wrapper stable — the
    // wrapper is what we style (opacity / z-index / pointer-events). If we passed
    // the wrapper directly, YT would detach it and our visibility + click routing
    // would land on a dead node (and clicks would hit the wrong frame).
    const mountA = document.createElement('div')
    const mountB = document.createElement('div')
    hostA.appendChild(mountA)
    hostB.appendChild(mountB)
    this.a = new Tube(mountA, {
      onReady: () => this.onFrameReady(),
      onStateChange: (e) => this.onState('A', e.data),
      onError: (e) => this.onFrameError('A', e.data),
    })
    this.b = new Tube(mountB, {
      onReady: () => this.onFrameReady(),
      onStateChange: (e) => this.onState('B', e.data),
      onError: (e) => this.onFrameError('B', e.data),
    })
  }

  init(): Promise<void> {
    return Promise.all([this.a.init(), this.b.init()]).then(() => {})
  }

  // ---- slot helpers ----
  private tube(slot: Slot): Tube {
    return slot === 'A' ? this.a : this.b
  }
  private host(slot: Slot): HTMLElement {
    return slot === 'A' ? this.hostA : this.hostB
  }
  private get standby(): Slot {
    return this.active === 'A' ? 'B' : 'A'
  }
  private get ready(): boolean {
    return this.a.ready && this.b.ready
  }

  private onFrameReady() {
    this.readyCount++
    if (this.readyCount === 2) {
      this.showOnly(this.active)
      this.cb.onReady?.()
    }
  }

  // Visibility: the active frame is opaque + on top and the ONLY one that takes
  // pointer events — this is what makes the "whack" land on the active iframe.
  // The standby stays hidden but live (so it can buffer) and click-through.
  private showOnly(slot: Slot) {
    const act = this.host(slot)
    const std = this.host(slot === 'A' ? 'B' : 'A')
    act.style.opacity = '1'
    act.style.zIndex = '2'
    act.style.pointerEvents = 'auto'
    std.style.opacity = '0'
    std.style.zIndex = '1'
    std.style.pointerEvents = 'none'
  }

  // ---- queue control ----

  /** True when queue entry `i` is an AD slot (no video to load). */
  private isAd(i: number): boolean {
    return this.queue[i] == null
  }

  /** Load a fresh channel queue. Entries may be video ids or null (ad slots). */
  setQueue(ids: QueueEntry[], startIndex = 0, play = false) {
    this.queue = ids.slice()
    this.index = this.clampIndex(startIndex)
    this.wantPlaying = play
    if (!this.ready) return
    // active gets the current video; standby pre-buffers the next.
    this.loadInto(this.active, this.index, play)
    this.cueStandby()
    this.emitProgram()
  }

  prepareQueue(ids: QueueEntry[], startIndex = 0) {
    this.queue = ids.slice()
    this.index = this.clampIndex(startIndex)
    this.wantPlaying = false
    if (!this.ready) return
    this.loadIntoBackground(this.active, this.index)
    this.cueStandby()
    this.emitProgram()
  }

  /**
   * Swap the queue WITHOUT reloading the active frame — for shuffle. The caller
   * guarantees ids[0] is the video currently playing, so we just adopt the new
   * order at index 0 and re-buffer the standby with the new "next". Playback of
   * the current video is never interrupted.
   */
  rebaseQueue(ids: QueueEntry[]) {
    this.queue = ids.slice()
    this.index = 0
    if (!this.ready) return
    this.cueStandby()
    this.emitProgram()
  }

  private clampIndex(i: number): number {
    if (!this.queue.length) return 0
    return ((i % this.queue.length) + this.queue.length) % this.queue.length
  }

  /** Cue `id` into `slot`, but skip it if the slot already holds that id as a
   *  FRESH cue (un-played, sitting at t=0). Re-cueing a warm standby would reset
   *  its buffer — the whole point of the double-buffer is to NOT do that. */
  private cueInto(slot: Slot, id: string, startSeconds = 0) {
    if (this.slotVideo[slot] === id && this.slotFresh[slot]) return
    if (startSeconds > 0) this.tube(slot).cueVideoById(id, startSeconds)
    else this.tube(slot).cueVideoById(id)
    this.slotVideo[slot] = id
    this.slotFresh[slot] = true
  }

  private loadInto(slot: Slot, qIndex: number, play: boolean) {
    const id = this.queue[qIndex]
    if (!id) return
    const t = this.tube(slot)
    t.setVolume(this.volume)
    if (play && this.audioUnlocked && !this.muted) t.unMute()
    else t.mute()
    if (play) {
      t.loadVideoById(id)
      this.slotVideo[slot] = id
      this.slotFresh[slot] = false // it's playing now, not a fresh t=0 cue
    } else {
      this.cueInto(slot, id)
    }
    t.setSubtitlesEnabled(this.subtitlesEnabled)
  }

  private loadIntoBackground(slot: Slot, qIndex: number) {
    const id = this.queue[qIndex]
    if (!id) return
    const t = this.tube(slot)
    t.setVolume(this.volume)
    t.mute()
    // Cue (never play) for the background so it doesn't run through an ad break;
    // cueInto adopts an already-warm standby instead of resetting its buffer.
    this.cueInto(slot, id)
    t.setSubtitlesEnabled(this.subtitlesEnabled)
  }

  // Pre-buffer the NEXT video into the standby frame (muted, cued). An ad slot
  // (null) loads nothing — the standby just sits blank until we reach it.
  private cueStandby() {
    if (!this.queue.length) return
    const next = this.clampIndex(this.index + 1)
    const id = this.queue[next]
    const t = this.tube(this.standby)
    t.mute()
    if (id) this.cueInto(this.standby, id)
    t.setSubtitlesEnabled(this.subtitlesEnabled)
  }

  // Hide BOTH frames (used when the active item is an ad — its scene is the
  // content, so no video should show through).
  private hideFrames() {
    for (const slot of ['A', 'B'] as Slot[]) {
      const h = this.host(slot)
      h.style.opacity = '0'
      h.style.pointerEvents = 'none'
    }
  }

  private emitProgram() {
    const t = this.tube(this.active)
    const data = t.getProgram()
    this.cb.onProgram?.({
      index: this.index,
      total: this.queue.length,
      videoId: this.queue[this.index] || '',
      title: data?.title || '',
      isAd: this.isAd(this.index),
    })
  }

  // Re-issue play() on a slot until it reaches PLAYING/BUFFERING. Guards against
  // a just-cued frame swallowing the first play() call. Self-cancels when a newer
  // kick starts or the slot stops being active.
  private kickPlay(slot: Slot) {
    if (this.playKickTimer) {
      window.clearTimeout(this.playKickTimer)
      this.playKickTimer = 0
    }
    const token = ++this.playKickToken
    let attempts = 0
    const tube = this.tube(slot)
    const tick = () => {
      if (token !== this.playKickToken) return // superseded by a newer transition
      if (slot !== this.active) return // we swapped away; abandon
      if (!this.wantPlaying) return // a deliberate pause superseded us; stop reconciling
      // Reconcile to ACTUALLY playing, not just "buffering". On mobile a freshly
      // cued frame often reports a transient BUFFERING and then stalls back to
      // paused/cued — so we keep re-issuing play() and peeking at the state until
      // it genuinely reports PLAYING, rather than trusting one signal.
      if (tube.getState() === YT_PLAYING) return // confirmed playing — done
      if (attempts++ > 80) {
        console.warn(`[DualPlayer] kickPlay timed out on slot ${slot} after ~12s — frame may be stuck`)
        Sentry.captureMessage(`DualPlayer kickPlay timeout`, { level: 'warning', extra: { slot, index: this.index, videoId: this.queue[this.index] } })
        return
      }
      if (!this.covered && this.audioUnlocked && !this.muted) tube.unMute()
      tube.play()
      this.playKickTimer = window.setTimeout(tick, 150)
    }
    tick()
  }

  // ---- frame events ----

  private onState(slot: Slot, data: number) {
    // Reactive tap: ANY frame moving (active or standby) wakes the reconciler, which
    // owns auto-advance now (a foreground ENDED → it advances the desired frame and
    // swaps). The player no longer self-advances internally.
    this.cb.onChange?.()
    if (slot !== this.active) return // ignore standby chatter
    if (data === YT_PLAYING) {
      this.resetActiveRetries() // it's genuinely playing — clear any reload retries
      if (!this.covered && this.audioUnlocked && !this.muted && this.tube(slot).isMuted()) {
        this.tube(slot).unMute()
        this.tube(slot).setVolume(this.volume)
      }
    }
  }

  private onFrameError(slot: Slot, errorCode?: number) {
    if (slot !== this.active) {
      const videoId = this.queue[this.clampIndex(this.index + 1)] || ''
      console.warn(`[DualPlayer] standby frame ${slot} error (code ${errorCode ?? '?'}) for video "${videoId}" — rebuilding standby`)
      Sentry.addBreadcrumb({ category: 'youtube.standby-error', message: `standby error code ${errorCode ?? '?'}`, data: { slot, videoId, errorCode }, level: 'warning' })
      this.cueStandby()
      return
    }
    const videoId = this.queue[this.index] || ''
    const t = this.tube(slot)
    // Many active-frame errors (incl. transient code-2 embed hiccups) clear on a
    // plain reload. Retry the SAME video a few times before giving up and skipping.
    if (videoId && this.activeRetryId !== videoId) {
      this.activeRetryId = videoId
      this.activeRetries = 0
    }
    if (videoId && this.activeRetries < MAX_LOAD_RETRIES) {
      this.activeRetries++
      const attempt = this.activeRetries
      console.warn(`[DualPlayer] active frame ${slot} error (code ${errorCode ?? '?'}) for video "${videoId}" — reload ${attempt}/${MAX_LOAD_RETRIES}`)
      Sentry.addBreadcrumb({ category: 'youtube.retry', message: `reload ${attempt}/${MAX_LOAD_RETRIES} code ${errorCode ?? '?'}`, data: { slot, videoId, errorCode, attempt }, level: 'warning' })
      if (this.activeRetryTimer) window.clearTimeout(this.activeRetryTimer)
      this.activeRetryTimer = window.setTimeout(() => {
        this.activeRetryTimer = 0
        // Still the same active video? reload it (masked/playing intent persists).
        if (this.active === slot && this.queue[this.index] === videoId) t.loadVideoById(videoId)
      }, LOAD_RETRY_BASE_MS * attempt)
      return
    }
    // Retries exhausted — capture context (a genuinely unplayable video shows
    // duration 0 / no seek; a bad live-seek shows pendingSeek past duration).
    const seekInfo = { pendingSeek: t.peekPendingSeek(), duration: t.getDuration(), currentTime: t.getCurrentTime() }
    console.warn(
      `[DualPlayer] active frame ${slot} error (code ${errorCode ?? '?'}) for video "${videoId}" — skipping after ${this.activeRetries} retries`,
      seekInfo,
    )
    Sentry.captureMessage(`DualPlayer active frame error`, {
      level: 'warning',
      extra: { slot, videoId, errorCode, index: this.index, attempts: this.activeRetries, ...seekInfo },
    })
    this.resetActiveRetries()
    // Tell the executor this video is dead; the reconciler routes around it (it owns
    // the queue position now — the player no longer self-advances).
    this.cb.onError?.(videoId)
  }

  /** Clear the active-frame reload-retry tracking (video changed or genuinely playing). */
  private resetActiveRetries() {
    this.activeRetries = 0
    this.activeRetryId = null
    if (this.activeRetryTimer) {
      window.clearTimeout(this.activeRetryTimer)
      this.activeRetryTimer = 0
    }
  }

  // ---- playback / audio controls (delegate to the active frame) ----

  play() {
    this.wantPlaying = true
    this.tube(this.active).play()
  }
  pause() {
    this.wantPlaying = false
    this.tube(this.active).pause()
  }
  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(100, v))
    this.a.setVolume(this.volume)
    this.b.setVolume(this.volume)
    if (this.muted || !this.audioUnlocked) {
      this.a.mute()
      this.b.mute()
    } else {
      this.tube(this.active).unMute()
      this.tube(this.standby).mute()
    }
  }
  mute() {
    this.muted = true
    this.a.mute()
    this.b.mute()
  }
  unMute() {
    this.muted = false
    this.tube(this.standby).mute()
    if (this.audioUnlocked) this.tube(this.active).unMute()
  }
  isMuted(): boolean {
    return this.tube(this.active).isMuted()
  }
  setSubtitlesEnabled(on: boolean) {
    if (this.subtitlesEnabled === on) return
    this.subtitlesEnabled = on
    const token = ++this.subtitlesRebuildToken
    const active = this.active
    const activeState = this.tube(active).getState()
    const shouldPlay = this.wantPlaying && (activeState === YT_PLAYING || activeState === YT_BUFFERING)
    const activeTime = this.tube(active).getCurrentTime()

    Promise.all([this.a.rebuild({ subtitles: on }), this.b.rebuild({ subtitles: on })])
      .then(() => {
        if (token !== this.subtitlesRebuildToken) return
        this.a.setVolume(this.volume)
        this.b.setVolume(this.volume)
        if (this.muted || !this.audioUnlocked) this.mute()
        else this.unMute()
        this.loadInto(this.active, this.index, shouldPlay)
        this.tube(this.active).restorePlaybackPosition(activeTime)
        this.cueStandby()
        this.showOnly(this.active)
        this.emitProgram()
      })
      .catch(() => {})
  }

  /** Mark audio unlocked (the whack landed) and apply to the active frame. */
  setAudioUnlocked(on: boolean) {
    this.audioUnlocked = on
    if (on && !this.muted) {
      const t = this.tube(this.active)
      t.unMute()
      t.setVolume(this.volume)
      if (this.wantPlaying) t.play()
    }
  }

  stop() {
    this.playKickToken++ // cancel any in-flight play-kick
    if (this.playKickTimer) {
      window.clearTimeout(this.playKickTimer)
      this.playKickTimer = 0
    }
    this.a.stop()
    this.b.stop()
  }

  // ---- queries ----

  /** The active frame's <iframe> — for whack/user-activation detection. */
  getActiveIframe(): HTMLIFrameElement | null {
    return this.tube(this.active).getIframeEl()
  }
  /** Current playback position of the active frame, in seconds. */
  getCurrentTime(): number {
    return this.tube(this.active).getCurrentTime()
  }
  hasQueue(): boolean {
    return this.queue.length > 0
  }
  getIndex(): number {
    return this.index
  }

  // ============================================================
  // REACTIVE MECHANISM SURFACE
  // ------------------------------------------------------------
  // The reconciler executor reads snapshot() and issues these
  // idempotent primitives. They reuse the same internals as the
  // FSM path (slots, cueInto, showOnly, kickPlay) but make NO
  // transition decisions — the brain decides, this just moves.
  // (Dormant until the executor is wired; the FSM path is intact.)
  // ============================================================

  /** Observe both frames for the reconciler's "actual" input. */
  snapshot(): DualSnapshot {
    const fg = this.tube(this.active)
    const bg = this.tube(this.standby)
    const bgId = this.slotVideo[this.standby]
    // CUED or further along ⇒ ready to swap in (cue preloads the player; the
    // stream spins up on play, exactly as today's swap does). UNSTARTED (-1)
    // means the cue hasn't landed yet → not ready.
    const bgReady = bgId != null && bg.getState() !== -1
    return {
      fgId: this.slotVideo[this.active],
      fgState: fg.getState(),
      fgTime: fg.getCurrentTime(),
      bgId,
      bgReady,
    }
  }

  /** Ensure the background frame holds `id`, cued + ready (idempotent: a warm standby already
   *  holding `id` is left untouched, never re-cued — so `atSec` only applies on the FIRST cue of a
   *  new id; the predictive offset is set once at stage time and the swap does the final
   *  correction, never re-cued per tick which would reset the buffer). `atSec` PRE-SEEKS the
   *  standby toward where live should be when it's revealed. */
  ensureBackground(id: string, atSec = 0) {
    if (!this.ready) return
    const t = this.tube(this.standby)
    t.setVolume(this.volume)
    t.mute()
    this.cueInto(this.standby, id, atSec)
    t.setSubtitlesEnabled(this.subtitlesEnabled)
  }

  /** Atomic swap: promote the background frame to foreground at queue `index`,
   *  seek it to the KNOWN offset `seekSec`, and play; silence + free the outgoing
   *  frame; pre-buffer the new next. No masked-play — we seek straight to where we
   *  should be. The cover (owned by the executor) masks the spin-up until the new
   *  frame reports PLAYING. */
  swapToBackground(index: number, seekSec: number) {
    if (!this.ready || !this.queue.length) return
    this.clearNonVideo() // a swap reveals video → tear down any scene/component layer (the wipe)
    this.presenters?.card.teardown() // …and the now-playing card: this is a NEW video, the old card is stale
    const incoming = this.standby
    const outgoing = this.active
    this.index = this.clampIndex(index)
    this.active = incoming
    this.slotFresh[incoming] = false
    this.wantPlaying = true
    const t = this.tube(incoming)
    t.setVolume(this.volume)
    if (seekSec > 0) t.restorePlaybackPosition(seekSec)
    // Stay silent if a cover is over us — reveal (setCovered(false)) lifts the mute.
    if (!this.covered && this.audioUnlocked && !this.muted) t.unMute()
    else t.mute()
    t.setSubtitlesEnabled(this.subtitlesEnabled)
    this.showOnly(incoming)
    this.kickPlay(incoming)
    this.tube(outgoing).mute()
    this.tube(outgoing).pause()
    this.cueStandby()
    this.emitProgram()
  }

  /** Seek the foreground to the KNOWN offset (live drift correction). Idempotent: a
   *  genuinely-playing frame already within SEEK_DEDUPE_S of the target is left alone, so
   *  an over-eager caller can't thrash the player (each seek round-trips through BUFFERING).
   *  Harmless for the swap-offset seek — that frame isn't PLAYING yet, so it still seeks. */
  seekForeground(seconds: number) {
    const t = this.tube(this.active)
    if (t.getState() === YT_PLAYING && Math.abs(t.getCurrentTime() - seconds) <= SEEK_DEDUPE_S) return
    t.seekTo(seconds)
  }

  /** Ensure the foreground is playing (idempotent). */
  playForeground() {
    this.wantPlaying = true
    this.clearNonVideo() // video is the content again → tear down any scene/component layer (the wipe)
    // ensure the active frame is VISIBLE — it may have been blanked by a preceding ad (blankFrames),
    // and an ad→same-video-id transition (a mid-content split half) maintains without a swap, so
    // nothing else un-hides it. showOnly is idempotent. (Under a bumper this is masked by the cover.)
    this.showOnly(this.active)
    this.tube(this.active).play()
  }

  /** Silence + freeze the foreground frame (idempotent). Used while a transition
   *  buffers the incoming frame in the background, so the outgoing channel/video
   *  can't be heard under the cover before the swap. */
  silenceForeground() {
    const t = this.tube(this.active)
    t.mute()
    t.pause()
  }

  /** A cover (bumper/ident) is over the foreground (`true`) or has been revealed
   *  (`false`). While covered the foreground is muted and the auto-unmute paths stay
   *  their hand, so audio can't leak under the bumper/snow; reveal lifts the mute. */
  setCovered(on: boolean) {
    if (this.covered === on) return // idempotent: the executor asserts this every tick
    this.covered = on
    const t = this.tube(this.active)
    if (on) t.mute()
    else if (this.audioUnlocked && !this.muted) {
      t.unMute()
      t.setVolume(this.volume)
    }
  }

  /** Hide both video frames (a scene/component is the content; the frames stay loaded + buffering). */
  blankFrames() {
    this.tube(this.active).mute()
    this.tube(this.standby).mute()
    this.tube(this.active).pause()
    this.hideFrames()
  }

  // ---- the Stage: own the non-video full-frame presentables ----

  /** Reveal VIDEO as the content — TEAR DOWN any scene/component layer (the wipe). Frame visibility is
   *  owned by the swap/playForeground that accompanies a video reveal, so this only clears the others. The
   *  teardowns are UNCONDITIONAL (both idempotent) so even a layer shown outside the Stage's tracking — the
   *  imperative locked-path cover — can't linger once video takes over. */
  private clearNonVideo() {
    this.presenters?.scene.teardown()
    this.presenters?.component.teardown()
    this.presentable = 'video'
  }

  /** Present a bump SCENE as the full-frame content (ad / ad-channel / the tune cover): silence + hide the
   *  video frames (kept warm + buffering the next video underneath), tear down the component if it was up,
   *  and show the scene. Idempotent dedup is the caller's job (the engine emits on scene-key change). */
  presentScene(scene: string, params: Record<string, unknown>, startSec = 0) {
    if (this.presentable === 'component') this.presenters?.component.teardown()
    this.presenters?.card.teardown() // a scene (cover / ad) replaces the video → the now-playing card is gone
    this.presentable = 'scene'
    this.silenceForeground()
    this.hideFrames()
    this.presenters?.scene.show(scene, params, startSec)
  }

  /** Present a React COMPONENT as the full-frame content (HIDEO). */
  presentComponent(component: string) {
    if (this.presentable === 'scene') this.presenters?.scene.teardown()
    this.presenters?.card.teardown() // a component replaces the video → the now-playing card is gone
    this.presentable = 'component'
    this.silenceForeground()
    this.hideFrames()
    this.presenters?.component.show(component)
  }

  /** Reveal VIDEO as the content: tear down any scene/component layer (the wipe) and un-hide the active
   *  frame. Idempotent — a co-emitted swap/playForeground already does both, this just makes it explicit. */
  revealVideo() {
    this.clearNonVideo()
    this.showOnly(this.active)
  }

  /** BLANK the Stage — NO full-frame content (no-signal channel / empty queue). Tear down any
   *  scene/component layer AND hide+silence the video frames, so the snow/black layer owns the screen
   *  with nothing of ours showing or sounding underneath. The explicit "no occupant" of the Stage. */
  blankStage() {
    this.clearNonVideo() // tear down any scene/component presentable
    this.blankFrames() // mute both frames, pause the active one, hide both (no stale frame under snow)
  }

  /** Set the active queue index without touching the frames (executor bookkeeping
   *  after it advances desired on a non-live ENDED — keeps emitProgram/cueStandby
   *  honest). */
  setIndex(index: number) {
    this.index = this.clampIndex(index)
  }

  /** Adopt a new queue WITHOUT loading either frame — the reconciler executor
   *  drives the foreground/background itself (ensureBackground → swap). The
   *  outgoing foreground keeps showing its old video until the executor swaps,
   *  so a cover can mask the change. `index` seeds where the queue "is" for
   *  getProgram/cueStandby bookkeeping. */
  adoptQueue(ids: QueueEntry[], index = 0) {
    this.queue = ids.slice()
    this.index = this.clampIndex(index)
  }
}
